import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '../ui';
import { IconPlus, IconX, IconDeviceFloppy } from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';

const sanitizeDecimalTyping = (val) => {
  if (val === null || val === undefined) return '';
  return String(val).replace(/[^0-9,.\-]/g, '');
};

const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
const readonlyClass = "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 cursor-not-allowed";

const InvoiceForm = ({
  formData, editingFacture, clients, cabinetInfo,
  handleSubmit, handleInputChange, handleItemChange,
  addItem, removeItem, setShowModal, resetForm, onToggleGlobal,
  workspaceStockEnabled, stockItems,
}) => {
  const { tenantType } = useAuth();
  const isMedical = tenantType === 'cabinet_medical';
  const invoiceLabel = isMedical ? "Note d'honoraire" : 'Facture';
  const isGlobal = formData.isGlobal === true;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-800">
            {editingFacture ? `Modifier la ${invoiceLabel}` : `Nouvelle ${invoiceLabel}`}
          </h3>
          <button onClick={() => { setShowModal(false); resetForm(); }}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <IconX size={18} strokeWidth={1.6} />
          </button>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit}>

            {/* Toggle Simple / Globale (médical uniquement) */}
            {isMedical && !editingFacture && (
              <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                <span className="text-xs font-medium text-gray-600">Type de note :</span>
                <button type="button" onClick={onToggleGlobal}
                  className={`px-4 py-1.5 text-xs font-medium rounded-lg border transition-colors ${!isGlobal ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                  Simple
                </button>
                <button type="button" onClick={onToggleGlobal}
                  className={`px-4 py-1.5 text-xs font-medium rounded-lg border transition-colors ${isGlobal ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                  Globale
                </button>
              </div>
            )}

            {/* Ligne 1 : Client + Numéro */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium mb-1.5 text-gray-600">
                  {isGlobal ? 'Cabinet / Caisse' : 'Client'} *
                </label>
                <select name="clientId" value={formData.clientId} onChange={handleInputChange}
                  className={inputClass}
                  required={!isGlobal}>
                  <option value="">{isGlobal ? 'Note globale (optionnel)' : 'Sélectionner un client'}</option>
                  {clients.map((clientItem) => {
                    const clientId = clientItem._id || clientItem.id;
                    return (
                      <option key={clientId} value={clientId}>
                        {clientItem.companyName}{clientItem.contactName ? ` - ${clientItem.contactName}` : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5 text-gray-600">Numéro *</label>
                <input type="text" name="number" value={formData.number} className={readonlyClass} readOnly required />
              </div>
            </div>

            {/* Ligne 2 : Dates */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium mb-1.5 text-gray-600">Date *</label>
                <input type="date" name="date" value={formData.date} onChange={handleInputChange} className={inputClass} required />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5 text-gray-600">Date d&apos;échéance *</label>
                <input type="date" name="dueDate" value={formData.dueDate} onChange={handleInputChange} className={inputClass} required />
              </div>
            </div>

            {/* Articles */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <label className="text-sm font-medium text-gray-700">Articles</label>
                <button type="button" onClick={addItem}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  <IconPlus size={13} strokeWidth={1.6} />
                  Ajouter un article
                </button>
              </div>

              <div className="grid grid-cols-12 gap-2 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {isGlobal && <div className="col-span-2">Patient</div>}
                <div className={isGlobal ? "col-span-2" : "col-span-2"}>Type</div>
                <div className={isGlobal ? "col-span-3" : "col-span-4"}>Description</div>
                <div className="col-span-1 text-center">Qté</div>
                <div className="col-span-2 text-center">Prix HT</div>
                <div className="col-span-1 text-center">TVA (%)</div>
                <div className="col-span-1"></div>
              </div>

              {formData.items.map((item, index) => {
                const taxDisplay = typeof item.tax === 'number' ? item.tax : item.itemType === 'debours' ? 0 : 19;
                return (
                  <div key={index} className="mb-3 border border-gray-100 rounded-lg p-2">
                    {workspaceStockEnabled && (
                      <div className="flex gap-2 mb-2 pb-2 border-b border-dashed border-gray-200">
                        <select
                          value={item.stockItemId || ''}
                          onChange={(e) => handleItemChange(index, 'stockItemId', e.target.value)}
                          className={`${inputClass} text-xs flex-1`}
                        >
                          <option value="">Article (stock)</option>
                          {stockItems.map((s) => {
                            const sid = s._id || s.id;
                            return (
                              <option key={sid} value={sid}>
                                {s.name} {s.sku ? `(${s.sku})` : ''} — {s.sellingPrice || 0} DT
                              </option>
                            );
                          })}
                        </select>
                        <button
                          type="button"
                          onClick={() => window.open('/stock/items', '_blank')}
                          className="px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 shrink-0"
                          title="Ajouter un article"
                        >
                          + Nouveau
                        </button>
                      </div>
                    )}
                    <div className="grid grid-cols-12 gap-2 items-center">
                    {isGlobal && (
                      <div className="col-span-2">
                        <input type="text" value={item.patientName || ''}
                          onChange={(e) => handleItemChange(index, 'patientName', e.target.value)}
                          className={inputClass} placeholder="Nom du patient" />
                      </div>
                    )}
                    <div className={isGlobal ? "col-span-2" : "col-span-2"}>
                      <select value={item.itemType || 'honoraire'} onChange={(e) => handleItemChange(index, 'itemType', e.target.value)} className={inputClass} required>
                        <option value="honoraire">Honoraire</option>
                        <option value="debours">Débours</option>
                      </select>
                    </div>
                    <div className={isGlobal ? "col-span-3" : "col-span-4"}>
                      <input type="text" value={item.description} onChange={(e) => handleItemChange(index, 'description', e.target.value)} className={inputClass} placeholder="Description" required />
                    </div>
                    <div className="col-span-1">
                      <input type="number" value={item.qty} onChange={(e) => handleItemChange(index, 'qty', e.target.value)} className={`${inputClass} text-center`} placeholder="Qté" min="1" step="1" required />
                    </div>
                    <div className="col-span-2">
                      <input type="text" inputMode="decimal" value={item.price ?? ''} onChange={(e) => handleItemChange(index, 'price', e.target.value)} className={`${inputClass} text-center`} placeholder="Prix HT" required />
                    </div>
                    <div className="col-span-1">
                      <select value={taxDisplay} onChange={(e) => handleItemChange(index, 'tax', parseFloat(e.target.value) || 0)} className={`${inputClass} text-center`}>
                        <option value={0}>0%</option>
                        <option value={7}>7%</option>
                        <option value={13}>13%</option>
                        <option value={19}>19%</option>
                      </select>
                    </div>
                    <div className="col-span-1">
                      {formData.items.length > 1 && (
                        <button type="button" onClick={() => removeItem(index)}
                          className="w-full p-2 text-red-500 hover:bg-red-50 border border-red-200 rounded-lg transition-colors flex items-center justify-center">
                          <IconX size={14} strokeWidth={1.6} />
                        </button>
                      )}
                    </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Totaux */}
            <div className="grid grid-cols-4 gap-4 mb-4">
              {[
                { label: 'Sous-total (HT)', name: 'subtotal', value: formData.subtotal },
                { label: 'Taxe totale (TVA)', name: 'totalTax', value: formData.totalTax },
                { label: 'Timbre fiscal', name: 'timbreFiscal', value: formData.timbreFiscal },
                { label: 'Total (TTC)', name: 'total', value: formData.total },
              ].map(({ label, name, value }) => (
                <div key={name}>
                  <label className="block text-xs font-medium mb-1.5 text-gray-600">{label}</label>
                  <input type="number" name={name} value={value} readOnly className={`${readonlyClass} ${name === 'total' ? 'font-semibold text-gray-800' : ''}`} />
                </div>
              ))}
            </div>

            {/* Statut */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium mb-1.5 text-gray-600">Statut</label>
                <select name="status" value={formData.status} onChange={handleInputChange} className={inputClass}>
                  <option value="sent">Envoyée</option>
                  <option value="paid">Payée</option>
                  <option value="cancelled">Annulée</option>
                </select>
              </div>
            </div>

            {/* Notes */}
            <div className="mb-6">
              <label className="block text-xs font-medium mb-1.5 text-gray-600">Notes</label>
              <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows="3"
                className={inputClass}
                placeholder={cabinetInfo?.defaultNotes || 'Notes supplémentaires...'} />
              {cabinetInfo?.defaultNotes && !formData.notes && (
                <p className="text-xs text-gray-400 mt-1">Note par défaut du cabinet disponible</p>
              )}
            </div>

            {/* Boutons */}
            <div className="flex gap-3 pt-4 border-t border-gray-100">
              <Button type="submit" variant="primary" className="flex-1">
                <IconDeviceFloppy size={15} strokeWidth={1.6} />
                {editingFacture ? `Modifier la ${invoiceLabel}` : `Créer la ${invoiceLabel}`}
              </Button>
              <Button type="button" variant="secondary" className="flex-1" onClick={() => { setShowModal(false); resetForm(); }}>
                Annuler
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

InvoiceForm.propTypes = {
  formData: PropTypes.shape({
    clientId: PropTypes.string, number: PropTypes.string, date: PropTypes.string, dueDate: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({ description: PropTypes.string, qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), tax: PropTypes.number, itemType: PropTypes.string, patientName: PropTypes.string, patientIdNumber: PropTypes.string, stockItemId: PropTypes.string })),
    total: PropTypes.number, subtotal: PropTypes.number, totalTax: PropTypes.number, timbreFiscal: PropTypes.number, status: PropTypes.string, notes: PropTypes.string, isGlobal: PropTypes.bool,
  }).isRequired,
  editingFacture: PropTypes.shape({ _id: PropTypes.string, clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ _id: PropTypes.string })]), number: PropTypes.string, date: PropTypes.string, dueDate: PropTypes.string, status: PropTypes.string, notes: PropTypes.string, items: PropTypes.array, isGlobal: PropTypes.bool }),
  clients: PropTypes.arrayOf(PropTypes.shape({ _id: PropTypes.string.isRequired, companyName: PropTypes.string.isRequired, contactName: PropTypes.string })).isRequired,
  cabinetInfo: PropTypes.shape({ defaultNotes: PropTypes.string }),
  handleSubmit: PropTypes.func.isRequired, handleInputChange: PropTypes.func.isRequired,
  handleItemChange: PropTypes.func.isRequired, addItem: PropTypes.func.isRequired,
  removeItem: PropTypes.func.isRequired, setShowModal: PropTypes.func.isRequired, resetForm: PropTypes.func.isRequired,
  onToggleGlobal: PropTypes.func,
  workspaceStockEnabled: PropTypes.bool,
  stockItems: PropTypes.array,
};

InvoiceForm.defaultProps = { editingFacture: null, cabinetInfo: null, onToggleGlobal: () => {}, workspaceStockEnabled: false, stockItems: [] };

export default InvoiceForm;
